<script>
    import { createEventDispatcher, onDestroy, onMount } from "svelte";
    import CardContent from "../card/CardContent/CardContent.svelte";
    import CardHeader from "../card/CardHeader/CardHeader.svelte";
    import CardTitle from "../card/CardTitle/CardTitle.svelte";
    import Editor from "../editor/Editor.svelte";
    import HighlighterFooter from "./HighlighterFooter.svelte";
    import CardTags from "../card/CardTags.svelte";
    import { getTagsFromComment } from "$/utils/Utils";
    import PageSelect, {
        PageSelectStyle,
    } from "../pageSelect/PageSelect.svelte";
    import {
        ActiveTopicStore,
        ActiveTopic,
        TopicAccessLevel,
    } from "$/stores/extension/ActiveTopicStore";
    import { ClusterStore } from "$/stores/ClusterStore";
    import HighlighterMessage from "./HighlighterMessage.svelte";
    import { TenantStore } from "$/stores/TenantStore";
    import { ExtensionStore } from "$/stores/ExtensionStore";
    import AiSuggestions from "../aiSuggestions/AiSuggestions.svelte";
    import { TokenStore } from "$/stores/TokenStore";
    import { PreferencesStore } from "$/stores/global/PreferencesStore";
    import { StoreStatus } from "$/models/StoreStatus";

    export let url;
    export let title;
    export let content;
    export let bookmark;
    export let highlight;

    const dispatch = createEventDispatcher();

    let isSaving = false;

    let comment = "";
    let mentions;
    let modalRef;

    let aiSuggestionsRef;

    let cardTitleRef;
    let showTag = false;

    let shouldFocusAiSuggestions = false;

    let card = {
        url: highlight?.url || url,
        nameSuggestion: title,
        name: bookmark ? title : null,
        content: highlight?.content || content,
        highlight: {
            anchorNode: highlight?.anchorNode,
            anchorOffset: highlight?.anchorOffset,
            focusNode: highlight?.focusNode,
            focusOffset: highlight?.focusOffset,
            pageNumber: highlight?.pageNumber,
            container: highlight?.container,
            preContent: highlight?.preContent,
            postContent: highlight?.postContent,
        },
        bookmark: bookmark,
        tags: [],
        originalId: highlight?.originalId,
    };

    //If there are no topics, extensionInit (called from HighlighterModal) will load the inbox cluster so to check if there is a snippet in inbox we can use ActiveTopicStore
    $: hasTopicOrSnippetInInbox =
        ($ClusterStore.clusters &&
            ClusterStore.getAllClusterTopics().length > 0) ||
        $ActiveTopic?.snippets.length > 0;

    $: debounceUpdateTags(comment);

    async function create() {
        isSaving = true;

        if (!hasTopicOrSnippetInInbox) {
            await ExtensionStore.createTopic($TenantStore?.active.id);
        }

        let savedSnippet = await ActiveTopicStore.createSnippet(card);

        card.tags.forEach((tag) => {
            ActiveTopicStore.addTagToSnippet(tag, savedSnippet.id);
        });

        // clean comment html
        let htmlComment = new DOMParser().parseFromString(comment, "text/html");
        let textComment = htmlComment.body.textContent || "";

        // if there is a comment, create it
        if (textComment.trim() !== "") {
            let commentRequest = {
                // HACK: Remove empty <p> tag at the end of comments due to Editor ENTER handling bug
                comment: comment.replace(/<p><\/p>$/g, ""),
                mentionMembers: mentions.getMentionedMembers(),
                mentionAndCreateMembers: mentions.getCreateAndMentionMembers(),
                mentionNonMembers: mentions.getMentionedNonMembers(),
            };
            let createdComment = await ActiveTopicStore.createComment(
                commentRequest,
                savedSnippet.id,
            );
            if (createdComment.mentionAndCreateMembers)
                createdComment.mentionAndCreateMembers.forEach((member) => {
                    TenantStore.addCollaborator(member.topicRole.user);
                });
            mentions.clear();
        }

        isSaving = false;
        dispatch("create", savedSnippet);
    }

    function addTagToHighlighter(newTag) {
        if (!card.tags.some((tag) => tag.id === newTag.id)) {
            card.tags = [...card.tags, newTag];
        }
    }

    function removeTagFromHighlighter(tagToRemove) {
        card.tags = card.tags.filter((tag) => tag.id !== tagToRemove.id);
    }

    // Update tags when comment changes
    let typingTimer;

    function debounceUpdateTags() {
        clearTimeout(typingTimer);
        typingTimer = setTimeout(async () => {
            const tagsFromComment = getTagsFromComment(comment);

            const mergedTags = [
                ...card.tags,
                ...tagsFromComment.filter(
                    (newTag) =>
                        !card.tags.some(
                            (existingTag) => existingTag.tag === newTag.tag,
                        ),
                ),
            ];

            card.tags = mergedTags;
        }, 500);
    }

    function updateCardTagList(updatedTagsList) {
        if (!updatedTagsList) return;

        card.tags = card.tags.flatMap(
            (tag) => updatedTagsList.find((t) => t.id === tag.id) || [],
        );
    }

    function handleAddTags(tags) {
        tags.forEach((tag) => {
            if (
                !card.tags.some(
                    (existingTag) => existingTag.tag === tag.tag,
                )
            ) {
                card.tags = [...card.tags, tag];
            }
        });
    }

    function handleEditorTabPressed() {
        aiSuggestionsRef?.focus();
    }

    function handleKeyDown(event) {
        if ((event.ctrlKey || event.metaKey) && event.key >= '1' && event.key <= '9') {
            event.preventDefault();
            aiSuggestionsRef?.applySuggestion(Number(event.key))
        }

        if (event.key === 'Escape') {
            dispatch("close");
        }

        if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
            create();
        }
    }

    function handleFocusOut(event) {
        // Check if focus is moving outside the modal wrapper
        if (modalRef && !modalRef.contains(event.relatedTarget)) {

            // Don't prevent the event - just restore focus after a short delay
            // This allows click events to work while still trapping focus
            setTimeout(() => {

                // Only restore focus if the modal is still visible and no element inside has focus
                if (modalRef && !modalRef.contains(document.activeElement)) {
                    modalRef.focus();
                }
            }, 0);
        }
    }

    const topicSubscription = ActiveTopic.subscribe((topic) => {
        if ($ActiveTopicStore.state.status === StoreStatus.UP_TO_DATE && card.tags.length)
            updateCardTagList(topic.tags);
    });

    onDestroy(() => {
        topicSubscription();
    })
</script>


{#if $PreferencesStore?.betaFeatures}
    <AiSuggestions
        {content}
        nameSuggestion={card.nameSuggestion}
        includeNameSuggestion={!card.bookmark}
        bind:this={aiSuggestionsRef}
        on:changeTitle={(data)=> card.name = data.detail}
        on:changeTopic={data => ActiveTopicStore.init(data.detail, TokenStore, TenantStore, ClusterStore)}
        on:addTags={data => handleAddTags(data.detail)}
    />
{/if}

{#if isSaving}
    <HighlighterMessage>Saving...</HighlighterMessage>
{:else}
    <div class="modal-wrapper"
        bind:this={modalRef}
        tabindex="-1"
        on:keydown|capture={handleKeyDown}
        on:focusout={handleFocusOut}>

        {#if hasTopicOrSnippetInInbox}
            <PageSelect style={PageSelectStyle.LIGHT} hideWorkspace={true} />
        {:else}
            <div class="topic-placeholder">
                {title}
            </div>
        {/if}

        <div class="modal-wrapper__spacer"></div>

        <div class="features">
            <CardTitle
                {card}
                editRole={TopicAccessLevel.OWNER}
                bind:this={cardTitleRef}
            />

            <div class="tags-wrapper">
                <CardTags
                    {card}
                    editRole={TopicAccessLevel.OWNER}
                    addTag={addTagToHighlighter}
                    removeTag={removeTagFromHighlighter}
                    bind:openSearch={showTag}
                />
            </div>

            <dvi class="show-feature-btns">
                <div
                    class="show-feature-btn"
                    on:click={() => cardTitleRef?.showTitle()}
                    tabindex="10"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke-width="1.5"
                        stroke="currentColor"
                        class="size-6"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            d="M12 7.5h1.5m-1.5 3h1.5m-7.5 3h7.5m-7.5 3h7.5m3-9h3.375c.621 0 1.125.504 1.125 1.125V18a2.25 2.25 0 0 1-2.25 2.25M16.5 7.5V18a2.25 2.25 0 0 0 2.25 2.25M16.5 7.5V4.875c0-.621-.504-1.125-1.125-1.125H4.125C3.504 3.75 3 4.254 3 4.875V18a2.25 2.25 0 0 0 2.25 2.25h13.5M6 7.5h3v3H6v-3Z"
                        />
                    </svg>
                    Title
                </div>

                <div
                    class="show-feature-btn"
                    on:click={() => (showTag = true)}
                    tabindex="11"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke-width="1.5"
                        stroke="currentColor"
                        class="size-6"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            d="M9.568 3H5.25A2.25 2.25 0 0 0 3 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 0 0 5.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 0 0 9.568 3Z"
                        />
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            d="M6 6h.008v.008H6V6Z"
                        />
                    </svg>
                    Tag
                </div>
            </dvi>
        </div>

        <CardContent {card} isGridCard={false} isExpanded={true} />

        <span class="modal-wrapper__line" tabindex="-1"></span>

        <!-- NOTE: This is needed because editor is initialized before
                parentWrapper is binded, think whether this should
                be moved to Editor itself. -->
        {#if modalRef}
            <Editor
                placeholder="Jot something about this..."
                action={create}
                bind:editorInputValue={comment}
                bind:mentions
                bind:parentWrapper={modalRef}
                on:tabPressed={handleEditorTabPressed}
                autoFocus={window.innerWidth > 768}
            />
        {/if}

        <HighlighterFooter
            {bookmark}
            on:cancel={() => {
                dispatch("close");
            }}
            on:confirm={create}
        />
    </div>
{/if}

<style>
    @import "../../main.css";

    .modal-wrapper {
        @apply overflow-clip rounded-xl outline-none;
    }

    .modal-wrapper__spacer {
        @apply flex w-full py-1;
    }

    .modal-wrapper__line {
        @apply my-2 flex w-full border-b border-gray-200;
    }

    .topic-placeholder {
        @apply truncate bg-gray-200 px-4 py-1 text-sm text-black;
    }

    .features {
        @apply flex flex-col gap-1;
    }

    .tags-wrapper {
        @apply relative;
    }

    .show-feature-btns {
        @apply flex gap-2 px-4;
    }

    .show-feature-btn {
        @apply flex cursor-pointer select-none items-center justify-center gap-1 rounded-sm border border-gray-200 px-2 py-1 text-xs hover:bg-gray-100;
    }

    .show-feature-btn svg {
        @apply h-4 w-4 min-w-4;
    }
</style>
